// v2 canteen — a dining lodge with a covered veranda: a half-timbered kitchen
// lodge across the back (stone chimney, serving hatch with a steaming pot,
// shuttered window), and a long pent-roofed veranda across the front sheltering
// two long tables that run front-to-back, each flanked by a pair of benches.
// Footprint-native 2x2. Ported 1:1 from age-of-mcp's v2/canteen.ts, using the
// library's shared `foundationSlab`, `barrel` and `village` (PLASTER, TIMBER,
// buildScaffold, gableRoof, shutterWindow) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, barrel, village, sitEat, leanLoad, smokePlume, EFFECT_LOOP, type Part } from "../../lib";

const BASE = 0.14;
const LZ = -0.55;            // lodge centre z (back of the plot)
const LODGE_W = 1.9, LODGE_D = 0.8;
const FRONT = LZ + LODGE_D / 2;   // -0.15 — the lodge's serving face
const HATCH_X = -0.5;        // serving hatch position on the front wall

// The veranda tables: two long tables running along z, benches at +/-x.
const TABLE_X = [-0.48, 0.52] as const;
const TABLE_Z = 0.33;

// The "kitchen is cooking" cue: smoke off the stone chimney (its stack tops out
// at y = 1.41) and steam rising off the stew pot at the serving hatch.
const plume = smokePlume([-0.62, 1.42, -0.72], { rise: 0.4 });
const steam = smokePlume([HATCH_X, 0.63, -0.07], {
  id: "steam", puffs: 2, rise: 0.18, radius: 0.035, drift: [0.02, 0.01], color: "#e4e4e4",
});

// The lodge body in its oak frame, `h` tall.
function lodge(h: number): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(LODGE_W, h, LODGE_D), color: village.PLASTER,
      position: [0, BASE + h / 2, LZ] },
  ];
  for (const [dx, dz] of [[-0.95, -0.40], [0.95, -0.40], [-0.95, 0.40], [0.95, 0.40]] as const) {
    out.push({ geometry: new THREE.BoxGeometry(0.07, h, 0.07), color: village.TIMBER,
      position: [dx, BASE + h / 2, LZ + dz] });
  }
  if (h > 0.5) {
    out.push({ geometry: new THREE.BoxGeometry(LODGE_W + 0.02, 0.05, LODGE_D + 0.02),
      color: village.TIMBER, position: [0, 0.50, LZ] });
  }
  return out;
}

// One long table (top on two trestles) plus its two flanking benches.
function diningRow(tx: number): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(0.30, 0.05, 0.72), color: "#9a7440",
      position: [tx, 0.40, TABLE_Z] },
    { geometry: new THREE.BoxGeometry(0.24, 0.26, 0.08), color: "#5e4528",
      position: [tx, 0.27, TABLE_Z - 0.22] },
    { geometry: new THREE.BoxGeometry(0.24, 0.26, 0.08), color: "#5e4528",
      position: [tx, 0.27, TABLE_Z + 0.22] },
  ];
  for (const side of [-1, 1] as const) {
    const bx = tx + side * 0.26;
    out.push(
      { geometry: new THREE.BoxGeometry(0.12, 0.04, 0.62), color: "#7a5a34",
        position: [bx, 0.26, TABLE_Z] },
      { geometry: new THREE.BoxGeometry(0.10, 0.12, 0.05), color: "#5e4528",
        position: [bx, 0.20, TABLE_Z - 0.24] },
      { geometry: new THREE.BoxGeometry(0.10, 0.12, 0.05), color: "#5e4528",
        position: [bx, 0.20, TABLE_Z + 0.24] },
    );
  }
  return out;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.94)];
  if (s === 1) {
    return [foundationSlab(1.94), ...lodge(0.32),
      ...village.buildScaffold(1.96, 0.86, 0.8, 0, LZ)];
  }
  if (s === 2) {
    return [foundationSlab(1.94), ...lodge(0.7),
      // Veranda posts go up ahead of their roof.
      { geometry: new THREE.BoxGeometry(0.06, 0.44, 0.06), color: village.TIMBER, position: [-0.9, 0.36, 0.78] },
      { geometry: new THREE.BoxGeometry(0.06, 0.44, 0.06), color: village.TIMBER, position: [0.9, 0.36, 0.78] },
      ...village.buildScaffold(1.96, 0.86, 1.35, 0, LZ)];
  }

  const wallH = 0.7;
  const top = BASE + wallH;            // 0.84
  const face = FRONT + 0.02;
  return [
    foundationSlab(1.94),
    ...lodge(wallH),
    // Gable roof over the lodge.
    ...village.gableRoof({ cx: 0, cz: LZ, topY: top, span: LODGE_D, length: LODGE_W, rise: 0.46 }),
    // Stone kitchen chimney, back-left; its smoke is the animated plume.
    { geometry: new THREE.BoxGeometry(0.18, 0.62, 0.18), color: "#6a6a6a",
      position: [-0.62, 1.10, -0.72] },
    ...plume.parts,
    // Serving hatch: dark opening, counter shelf, steaming stew pot.
    { geometry: new THREE.BoxGeometry(0.44, 0.28, 0.05), color: "#2a2018",
      position: [HATCH_X, 0.60, face] },
    { geometry: new THREE.BoxGeometry(0.50, 0.05, 0.14), color: "#9a7440",
      position: [HATCH_X, 0.44, face + 0.05] },
    { geometry: new THREE.CylinderGeometry(0.09, 0.075, 0.12, 8), color: "#3a3a3a",
      position: [HATCH_X, 0.525, face + 0.06] },
    { geometry: new THREE.CylinderGeometry(0.085, 0.085, 0.025, 8), color: "#c66a2c",
      position: [HATCH_X, 0.595, face + 0.06] },
    ...steam.parts,
    // Door and a shuttered window on the front wall.
    { geometry: new THREE.BoxGeometry(0.30, 0.55, 0.05), color: "#5a3c24",
      position: [0.15, 0.415, face] },
    ...village.shutterWindow(0.72, 0.58, face),
    // The covered veranda: a pent roof from the lodge front out over the
    // dining rows, carried on four posts.
    { geometry: new THREE.BoxGeometry(1.96, 0.05, 0.975), color: "#8a5a34",
      position: [0, 0.69, 0.325], rotation: [0.2277, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.06, 0.44, 0.06), color: village.TIMBER, position: [-0.9, 0.36, 0.78] },
    { geometry: new THREE.BoxGeometry(0.06, 0.44, 0.06), color: village.TIMBER, position: [-0.3, 0.36, 0.78] },
    { geometry: new THREE.BoxGeometry(0.06, 0.44, 0.06), color: village.TIMBER, position: [0.3, 0.36, 0.78] },
    { geometry: new THREE.BoxGeometry(0.06, 0.44, 0.06), color: village.TIMBER, position: [0.9, 0.36, 0.78] },
    // Two long dining rows under the veranda.
    ...diningRow(TABLE_X[0]),
    ...diningRow(TABLE_X[1]),
    // A water barrel at the hatch end of the veranda.
    barrel(-0.85, 0.02),
  ];
}

export const canteenV2 = defineModel({
  id: "canteen-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Canteen (v2)",
  tags: ["building","canteen","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly canteen.",
  description: "A mess hall where workers eat, kept stocked with food.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Kitchen cooking",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [...plume.tracks, ...steam.tracks],
    },
  ],
  stations: [
    { id: "seat-0", position: [-0.74, 0.28, 0.16], facing: Math.PI / 2 },
    { id: "seat-1", position: [-0.74, 0.28, 0.5], facing: Math.PI / 2 },
    { id: "seat-2", position: [-0.22, 0.28, 0.16], facing: -Math.PI / 2 },
    { id: "seat-3", position: [-0.22, 0.28, 0.5], facing: -Math.PI / 2 },
    { id: "seat-4", position: [0.26, 0.28, 0.16], facing: Math.PI / 2 },
    { id: "seat-5", position: [0.26, 0.28, 0.5], facing: Math.PI / 2 },
    { id: "seat-6", position: [0.78, 0.28, 0.16], facing: -Math.PI / 2 },
    { id: "seat-7", position: [0.78, 0.28, 0.5], facing: -Math.PI / 2 },
    { id: "serve", position: [-0.5, 0.14, 0.1], facing: Math.PI, description: "The serving window." },
  ],
  interactions: [
    { id: "dine", label: "Eating", station: "seat-0", variants: ["complete"], ...sitEat() },
    { id: "load", label: "Loading supplies", station: "serve", variants: ["complete"], ...leanLoad() },
  ],
});
